import React, { useEffect, useMemo } from "react";

import {
  useLazyHoldNewCabinQuery,
  useLazyRetrieveAvailableCabinsQuery,
  useLazyRetrieveAvailableGradesQuery,
  useLazyUpdateNewCabinQuery,
} from "../../../../../store/services/ReservationService";
import { hideModal } from "../../../../../store/slices/modalSlice";
import {
  resetAvailableCabins,
  resetEditCabinStateToInitial,
  setSelectedCabin,
  setSelectedGrade,
} from "../../../../../store/slices/reservationSlice";
import { showToast } from "../../../../../store/slices/toastSlice";
import { useTypedDispatch, useTypedSelector } from "../../../../../store/store";
import TOAST from "../../../../../utils/constants/toast";
import { currencyToFormat } from "../../../../../utils/helpers/currency";
import Alert from "../../../../shared/Alert";
import Button from "../../../../shared/Button";
import CustomSelect from "../../../../shared/CustomSelect";

import styles from "./index.module.scss";

function EditCabinModal() {
  const dispatch = useTypedDispatch();

  const [retrieveAvailableGradesQuery, { isFetching: isGradesLoading }] =
    useLazyRetrieveAvailableGradesQuery();

  const [retrieveAvailableCabinsQuery, { isFetching: isCabinsLoading }] =
    useLazyRetrieveAvailableCabinsQuery();

  const [holdNewCabin, { isFetching: isHoldNewCabinLoading }] =
    useLazyHoldNewCabinQuery();

  const [updateNewCabin, { isFetching: isUpdateNewCabinLoading }] =
    useLazyUpdateNewCabinQuery();

  const { data, type } = useTypedSelector((state) => state.modal);

  const { availableGrades, availableCabins, selectedGrade, selectedCabin } =
    useTypedSelector((state) => state.reservation);

  const gradesSelectOptions = useMemo(() => {
    if (!availableGrades || !availableGrades.length) {
      return [];
    }

    const output = availableGrades.map((grade) => ({
      value: grade.code,
      label: `${grade.code} - ${grade.name} ${grade.total_price && ` (${currencyToFormat(grade?.total_price, data?.reservation?.currency ?? "USD")})`}`,
    }));

    return output;
  }, [availableGrades, selectedGrade]);

  const cabinsSelectOptions = useMemo(() => {
    if (!availableCabins || !availableGrades.length) {
      return [];
    }

    const output = availableCabins.map((cabin) => ({
      value: cabin.uuid,
      label: `${cabin.number} - ${cabin.deck_name} (${cabin.position}/${cabin.location}, Max: ${cabin.maximum_occupancy} guests) ${cabin.cabin_size && `- ${cabin.cabin_size} ${cabin.cabin_measurement}`}`,
    }));

    return output;
  }, [availableCabins, selectedCabin]);

  const initGrades = async () => {
    const params: any = {
      channel: "CS",
      rate_code: data?.sailing?.rate_code ?? "",
      sailing_code: data?.sailing?.sailing_code ?? "",
      grade_code: data?.cabin?.grade_code ?? "",
      channel_partner: data?.reservation?.agency ?? "",
      agency: data?.reservation?.agent ?? "",
      guest_count: String(data?.cabin?.guests?.length ?? 1),
    };

    data?.cabin?.guests?.forEach((guest: any, index: number) => {
      const guestNum = index + 1;

      params[`guest_dob_${guestNum}`] = guest.date_of_birth ?? "";
    });

    await retrieveAvailableGradesQuery(params);

    if (data?.cabin?.grade_code) {
      await dispatch(setSelectedGrade(data.cabin.grade_code));
    }
  };

  const initCabins = async () => {
    const params: any = {
      channel: "CS",
      rate_code: data?.sailing?.rate_code ?? "",
      sailing_code: data?.sailing?.sailing_code ?? "",
      grade_code: selectedGrade,
      channel_partner: data?.reservation?.agency ?? "",
      agency: data?.reservation?.agent ?? "",
      guest_count: String(data?.cabin?.guests?.length ?? 1),
    };

    data?.cabin?.guests?.forEach((guest: any, index: number) => {
      const guestNum = index + 1;

      params[`guest_dob_${guestNum}`] = guest.date_of_birth ?? "";
    });

    await retrieveAvailableCabinsQuery(params);
  };

  const handleGradeChange = ({ value: grade }: { value: string }) => {
    dispatch(setSelectedGrade(grade));
    dispatch(setSelectedCabin(""));
    dispatch(resetAvailableCabins());
  };

  const handleCabinChange = ({ value: cabin }: { value: string }) => {
    dispatch(setSelectedCabin(cabin));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const newCabin = availableCabins.find((c) => c.uuid === selectedCabin);

      if (!newCabin) {
        throw new Error("Selected cabin not found");
      }

      const holdPayload = {
        sailing_code: data?.sailing?.sailing_code,
        rate_code: data?.sailing?.rate_code,
        grade_code: newCabin.grade_code,
        cabin_number: newCabin.number,
        agency: data?.reservation?.agent ?? "",
        channel: "CS",
        channel_partner: data?.reservation?.agency ?? "",
      };

      const editPayload = {
        sailing_uuid: data?.sailing?.uuid ?? "",
        pnr: data?.reservation?.pnr ?? "",
        channel: "CS",
        channel_partner: data?.reservation?.agency ?? "",
        agency: data?.reservation?.agent ?? "",
        rate_code: data?.sailing?.rate_code,
        cabin: {
          uuid: data?.cabin.uuid,
          grade_code: newCabin.grade_code,
          cabin_number: newCabin.number,
          guests: data?.cabin?.guests,
        },
        reprice: true,
        commit: true,
      };

      await holdNewCabin(holdPayload);
      await updateNewCabin({ ...editPayload, cabinUuid: data?.cabin?.uuid });

      dispatch(hideModal());

      dispatch(
        showToast({
          type: TOAST.SUCCESS_TYPE,
          message: `Cabin successfully changed from ${data?.cabin?.cabin_number} to ${newCabin.number}`,
          duration: TOAST.DEFAULT_DURATION,
        }),
      );
    } catch {
      dispatch(
        showToast({
          type: TOAST.ERROR_TYPE,
          message: "Failed to update cabin",
          duration: TOAST.DEFAULT_DURATION * 2,
        }),
      );
    }
  };

  useEffect(() => {
    if (data) {
      (async () => await initGrades())();
    }
  }, [data]);

  useEffect(() => {
    if (selectedGrade) {
      (async () => await initCabins())();
    }
  }, [selectedGrade]);

  useEffect(() => {
    if (type === "") {
      setTimeout(() => {
        dispatch(resetEditCabinStateToInitial());
      }, 500);
    }
  }, [type]);

  return (
    <div className={styles.container}>
      <div className={styles.alerts}>
        <Alert
          message={`Current Cabin: ${data?.cabin?.grade_code} / ${data?.cabin?.cabin_number} - Deck: ${data?.cabin?.deck_name}`}
        />
      </div>

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.inputs}>
          <CustomSelect
            className={styles.input}
            value={selectedGrade}
            label="select new grade"
            items={gradesSelectOptions}
            placeholder="Choose a grade..."
            onChange={handleGradeChange}
            disabled={
              !availableGrades ||
              availableGrades?.length === 0 ||
              isGradesLoading ||
              isHoldNewCabinLoading ||
              isUpdateNewCabinLoading
            }
          />

          <CustomSelect
            className={styles.input}
            value={selectedCabin}
            label="select new cabin"
            items={cabinsSelectOptions}
            placeholder="Choose a cabin..."
            onChange={handleCabinChange}
            disabled={
              !availableCabins ||
              availableCabins?.length === 0 ||
              !selectedGrade ||
              isGradesLoading ||
              isCabinsLoading ||
              isHoldNewCabinLoading ||
              isUpdateNewCabinLoading
            }
          />

          <Button
            className={styles.button}
            label="change cabin"
            type="submit"
            loading={isHoldNewCabinLoading || isUpdateNewCabinLoading}
            disabled={
              isGradesLoading ||
              isCabinsLoading ||
              isHoldNewCabinLoading ||
              isUpdateNewCabinLoading ||
              !selectedGrade ||
              !selectedCabin
            }
          />
        </div>
      </form>
    </div>
  );
}

export default EditCabinModal;
